import axios from "axios";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GoogleAuthProvider, getIdToken, signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";

import { Context } from "../../context/context";
import { auth } from "../../firebase/firebase.utils";
import AuthLayout from "../../components/layout/auth-layout.component";
import { Button, Field, TextInput } from "../../components/ui";
import GoogleLogo from "../../assets/google.png";

const EMAIL_ERRORS = {
  "auth/invalid-email": "The email address is invalid.",
  "auth/user-disabled": "This account has been disabled.",
  "auth/user-not-found": "No MedpeR account exists for that email.",
  "auth/wrong-password": "Incorrect password.",
  "auth/invalid-credential": "That email and password don't match.",
  "auth/too-many-requests": "Too many attempts. Try again in a few minutes.",
};

const POPUP_ERRORS = {
  "auth/popup-blocked": "Popup blocked. Please allow popups for this site.",
  "auth/popup-closed-by-user": "Popup closed before sign-in finished.",
  "auth/cancelled-popup-request": "Popup closed before sign-in finished.",
};

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { dispatch } = useContext(Context);
  const navigate = useNavigate();

  /** Exchange the Firebase ID token for a MedpeR session. */
  const completeSignIn = async (user, toastId) => {
    const idToken = await getIdToken(user);
    const { data } = await axios.post("/app-api/user/login", { idToken });
    await auth.signOut();
    dispatch({ type: "LOGIN", payload: data });
    window.sessionStorage.setItem("user", JSON.stringify(data));
    toast.update(toastId, {
      render: `Authenticated as ${data.email}`,
      type: "success",
      isLoading: false,
      autoClose: 2000,
      delay: 100,
    });
    navigate("/");
  };

  const fail = (toastId, message) =>
    toast.update(toastId, { render: message, type: "error", isLoading: false, autoClose: 2000, delay: 100 });

  const handleEmailSignIn = async (event) => {
    event.preventDefault();
    const toastId = toast.loading("Authenticating...");
    setIsLoading(true);
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      await completeSignIn(user, toastId);
    } catch (err) {
      console.error(err);
      fail(toastId, EMAIL_ERRORS[err.code] || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const toastId = toast.loading("Authenticating...");
    setIsLoading(true);
    try {
      const { user } = await signInWithPopup(auth, new GoogleAuthProvider());
      await completeSignIn(user, toastId);
    } catch (err) {
      console.error(err);
      fail(toastId, POPUP_ERRORS[err.code] || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back. Enter your details to continue."
      footer={
        <p className="text-sm text-gray-500">
          No account?{" "}
          <button onClick={() => navigate("/signup")} className="font-semibold text-primary-600 hover:underline">
            Create one
          </button>
        </p>
      }
    >
      <form onSubmit={handleEmailSignIn}>
        <Field label="Email" htmlFor="signin-email">
          <TextInput
            id="signin-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            placeholder="name@example.com"
            onChange={(e) => setEmail(e.target.value.trim())}
          />
        </Field>

        <Field label="Password" htmlFor="signin-password">
          <TextInput
            id="signin-password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            placeholder="••••••••"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        <div className="mb-5 flex justify-end">
          <button
            type="button"
            onClick={() => navigate("/forgotpassword")}
            className="text-xs font-semibold text-primary-600 hover:underline"
          >
            Forgot password?
          </button>
        </div>

        <Button type="submit" size="lg" loading={isLoading} className="w-full">
          Sign in
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">or</span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <Button
        type="button"
        variant="secondary"
        size="lg"
        onClick={handleGoogleSignIn}
        disabled={isLoading}
        className="w-full"
      >
        <img src={GoogleLogo} alt="" aria-hidden="true" className="h-4 w-4 object-contain" />
        Sign in with Google
      </Button>
    </AuthLayout>
  );
};

export default SignIn;
